import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const isValidPostcode = (pc) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(pc.trim());
const money = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(2));

export default function ParkingVsTaxiReading() {
  const [form, setForm] = useState({
    pickup_postcode: "",
    dropoff_terminal: "LHR5",
    passengers: 4,
    luggage: 3,
    days_parked: 7,
    pickup_datetime: ""
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextMondayAt11 = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 7;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(11, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm((prev) => ({
      ...prev,
      pickup_postcode: "RG1 2AG",
      dropoff_terminal: "LHR5",
      passengers: 4,
      luggage: 3,
      days_parked: 7,
      pickup_datetime: nextMondayAt11()
    }));
  }, []);

  const submit = async () => {
    const pc = form.pickup_postcode.trim().toUpperCase();
    if (!pc || !isValidPostcode(pc)) {
      setError("Please enter a valid UK pickup postcode.");
      return;
    }
    if (!pc.startsWith("RG")) {
      setError(null);
      setResult(null);
      setNotice("⚠️ This page is optimised for Reading (RG postcodes). For other areas, use the main parking vs taxi calculator.");
      return;
    }
    if (!form.pickup_datetime) {
      setError("Please select a pickup date and time.");
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      const payload = {
        ...form,
        pickup_postcode: pc,
        passengers: Math.min(6, Math.max(1, Number(form.passengers) || 4)),
        luggage: Math.min(6, Math.max(0, Number(form.luggage) || 3)),
        days_parked: Math.min(21, Math.max(1, Number(form.days_parked) || 7))
      };
      const res = await api.post("/api/parking/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.pickup_datetime]);

  const parkingTotal = result?.costs?.parking?.total ?? null;
  const taxiTwoWay = result?.costs?.taxi?.cheapcabgrab_two_way ?? null;
  const delta = parkingTotal != null && taxiTwoWay != null ? Number((parkingTotal - taxiTwoWay).toFixed(2)) : null;
  const heroVerdict = useMemo(() => {
    if (delta == null) return "Compare complete for this Reading → Heathrow trip.";
    if (Math.abs(delta) < 5) return "Costs are similar for this Reading → Heathrow trip — choose based on fatigue and convenience.";
    if (delta < 0) return `✅ Best choice for this Reading → Heathrow trip: Parking (about £${money(Math.abs(delta))} cheaper than two taxis).`;
    return `🚖 Best choice for this Reading → Heathrow trip: Taxi (about £${money(Math.abs(delta))} cheaper than parking once fuel/time are included).`;
  }, [delta]);

  const listStyle = { paddingLeft: 18, lineHeight: 1.7, color: "#1f2937", fontSize: 15, marginTop: 6, marginBottom: 6 };
  const sectionTitleStyle = { marginTop: 20, marginBottom: 8, fontSize: 18, fontWeight: 800, color: "#0f172a" };
  const bodyTextStyle = { fontSize: 15, color: "#1f2937", lineHeight: 1.6, marginTop: 4, marginBottom: 4 };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Reading to Heathrow Parking vs Taxi (2026 Cost Comparison)</title>
        <meta
          name="description"
          content="Compare the real cost of parking vs taking a taxi from Reading to Heathrow. Includes fuel, 7-day parking, return taxi fares, and travel hassle — updated for 2026."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/reading-heathrow-parking-vs-taxi" />
        <meta property="og:title" content="Reading to Heathrow Parking vs Taxi (2026 Cost Comparison)" />
        <meta
          property="og:description"
          content="Is it cheaper to park or take a taxi from Reading to Heathrow? See live costs, fuel, 7-day parking, and hassle scores for your trip."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "Is parking cheaper than a taxi from Reading to Heathrow?", acceptedAnswer: { "@type": "Answer", text: "It depends on distance, duration, and passengers. This calculator shows the cheapest option for your Reading postcode." } },
              { "@type": "Question", name: "How far is Reading from Heathrow by road?", acceptedAnswer: { "@type": "Answer", text: "Reading is around 25–30 miles from Heathrow depending on postcode and route. Exact distance is calculated live below." } },
              { "@type": "Question", name: "How much is a taxi from Reading to Heathrow?", acceptedAnswer: { "@type": "Answer", text: "Taxi costs vary by provider. We show live comparison for two trips (outbound and return) for your postcode." } },
              { "@type": "Question", name: "Is Long Stay parking worth it for Reading travellers?", acceptedAnswer: { "@type": "Answer", text: "Often for 7+ day trips. Check the calculator to see if parking beats two taxis for your dates." } },
              { "@type": "Question", name: "What happens if my return flight is delayed?", acceptedAnswer: { "@type": "Answer", text: "Parking time may increase; taxis avoid extra parking time. Re-run the calculator if timings change." } },
              { "@type": "Question", name: "Is a taxi better for families travelling from Reading?", acceptedAnswer: { "@type": "Answer", text: "Taxis remove post-flight driving fatigue; the calculator shows the price trade-off for your Reading trip." } }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>Reading to Heathrow: Parking vs Taxi Cost Comparison (2026)</h1>
        <p style={{ fontSize: 15, color: "#0f172a", marginBottom: 6 }}>
          Travelling from Reading to Heathrow? This calculator compares parking and taxi for your trip using real distances, fuel, and live pricing.
        </p>
        <p style={{ fontSize: 13, color: "#475467", marginBottom: 6 }}>
          Reading is around 25–30 miles from Heathrow, depending on postcode and route. Exact distance and costs are calculated live below.
        </p>
        <p style={{ fontSize: 14, color: "#374151", marginBottom: 0 }}>
          Example calculation for Reading. Change the postcode if you’re elsewhere.
        </p>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f8fafc" }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>The quick answer for Reading</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", marginTop: 8 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use PARKING if:</div>
            <ul style={listStyle}>
              <li>You’re staying 7+ days and fine driving home.</li>
              <li>You prefer your own car and flexible timing.</li>
              <li>Your Reading postcode is farther out (fuel spreads the cost).</li>
            </ul>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use TAXI if:</div>
            <ul style={listStyle}>
              <li>You want zero post-flight driving fatigue.</li>
              <li>You’re closer in Reading or travelling with family.</li>
              <li>You want door-to-door over parking shuttles.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="parking-form" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, marginTop: 14 }}>
        <div className="form-group">
          <label>Pickup postcode (Reading prefilled, editable)</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            onChange={(e) => setForm({ ...form, pickup_postcode: e.target.value })}
            placeholder="e.g. RG1 2AG"
          />
        </div>

        {notice && (
          <div style={{ marginTop: 8, background: "#fff7ed", border: "1px solid #fed7aa", padding: 10, borderRadius: 10, color: "#9a3412" }}>
            {notice}{" "}
            <a href="/heathrow-parking-vs-taxi" style={{ fontWeight: 700, color: "#9a3412", textDecoration: "underline" }}>
              Open full parking vs taxi calculator →
            </a>
          </div>
        )}

        <div className="form-group">
          <label>Terminal</label>
          <select
            className="parking-input"
            value={form.dropoff_terminal}
            onChange={(e) => setForm({ ...form, dropoff_terminal: e.target.value })}
          >
            <option value="LHR2">Terminal 2</option>
            <option value="LHR3">Terminal 3</option>
            <option value="LHR4">Terminal 4</option>
            <option value="LHR5">Terminal 5</option>
          </select>
        </div>

        <div className="form-group">
          <label>Passengers</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="6"
            value={form.passengers}
            onChange={(e) => setForm({ ...form, passengers: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Luggage</label>
          <input
            type="number"
            className="parking-input"
            min="0"
            max="6"
            value={form.luggage}
            onChange={(e) => setForm({ ...form, luggage: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Days parked (you can change this)</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="21"
            value={form.days_parked}
            onChange={(e) => setForm({ ...form, days_parked: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Pickup date & time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
          />
        </div>

        <button className="parking-btn" onClick={submit}>
          {loading ? "Calculating…" : "Compare parking vs taxi from Reading"}
        </button>

        {error && <p style={{ color: "red", marginTop: 8 }}>{error}</p>}
      </div>

      {result && (
        <div className="parking-results">
          <div
            style={{
              border: "1px solid #16a34a",
              borderRadius: 14,
              padding: 16,
              background: "#ecfdf5",
              boxShadow: "0 8px 20px rgba(22,163,74,0.08)"
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18 }}>{heroVerdict}</div>
            <div style={{ marginTop: 6, fontSize: 14, color: "#1f2937" }}>
              Live calculation for {result.journey?.pickup_postcode} → {result.costs?.parking?.terminal || result.journey?.dropoff_terminal}. Includes fuel, 7-day parking default, taxi return, and hassle indicators.
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 8 }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Parking total</div>
                <div style={{ fontSize: 18 }}>{money(parkingTotal)}</div>
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Two taxis (return)</div>
                <div style={{ fontSize: 18 }}>{money(taxiTwoWay)}</div>
              </div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#475467" }}>
              Results vary by distance, passengers, and return-journey fatigue.
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Cost comparison: parking vs two taxi journeys</div>
            <table className="parking-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Cost (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>🚗 Parking total</td>
                  <td>{money(result.costs?.parking?.total)}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Parking price ({result.costs?.parking?.pricing_mode || "—"})</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.parking_price)}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Fuel estimate (return driving)</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.fuel_price)}</td>
                </tr>
                <tr>
                  <td>🚖 CheapCabGrab (2 trips)</td>
                  <td>{money(result.costs?.taxi?.cheapcabgrab_two_way)}</td>
                </tr>
                <tr>
                  <td>📱 Ride-hail range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.ride_hail_two_way
                      ? `£${money(result.costs.taxi.ride_hail_two_way.min)} – £${money(result.costs.taxi.ride_hail_two_way.max)}`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>🚕 Black cab range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.black_cab_two_way_range
                      ? `£${money(result.costs.taxi.black_cab_two_way_range.min)} – £${money(result.costs.taxi.black_cab_two_way_range.max)}`
                      : result.costs?.taxi?.black_cab_two_way != null
                        ? `~£${money(result.costs.taxi.black_cab_two_way)}`
                        : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
              Want the full tool? <a href="/heathrow-parking-vs-taxi">Open the main parking vs taxi calculator</a> or try the <a href="/7-day-heathrow-parking-vs-taxi">7-day comparison</a>. Looking for free drop-off? <a href="/heathrow-dropoff-free">See the free drop-off guide</a>.
            </div>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Why Reading is a “toss-up” route</div>
            <ul style={listStyle}>
              <li>Mid-range distance (~25–30 miles) means small changes flip the result.</li>
              <li>Taxi fares aren’t extreme; parking costs are competitive.</li>
              <li>Fuel, passengers, and delays swing the cheapest option.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>When parking usually wins from Reading</div>
            <ul style={listStyle}>
              <li>Longer stays (7+ days).</li>
              <li>Single driver, light luggage, comfortable with the return drive.</li>
              <li>Postcodes farther out where fuel spreads the cost.</li>
            </ul>

            <div style={{ fontWeight: 800, margin: "14px 0 6px" }}>When a taxi is often better from Reading</div>
            <ul style={listStyle}>
              <li>Families or groups wanting zero parking stress.</li>
              <li>Late arrivals or flight delay risk.</li>
              <li>Door-to-door convenience matters more than driving yourself.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f8fafc" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Real examples (re-run with your postcode)</div>
            <ul style={listStyle}>
              <li>Central Reading (RG1) → T5: Taxi can be competitive; parking wins if you’re fine driving back.</li>
              <li>South Reading (RG2) → T3: Parking often cheaper for 7-day stays; taxi removes post-flight driving.</li>
              <li>North Reading (RG4) → T2/T5: Mid-range—fuel and luggage swing the decision.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Use the calculator for your exact trip</div>
            <p style={bodyTextStyle}>
              This page starts with a Reading example. Change the postcode, terminal, days, or date/time to personalise. For 7-day-focused guidance, try the <a href="/7-day-heathrow-parking-vs-taxi">7-day comparison</a>.
            </p>
            <p style={bodyTextStyle}>
              Need free drop-off info? <a href="/heathrow-dropoff-free">See the free drop-off guide</a>.
            </p>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Reading-specific FAQs</div>
            <div style={{ display: "grid", gap: 8 }}>
              {[
                { q: "Is parking cheaper than a taxi from Reading to Heathrow?", a: "It depends on distance, days, and passengers. This calculator shows the cheapest option for your Reading postcode." },
                { q: "How far is Reading from Heathrow by road?", a: "Around 25–30 miles depending on postcode and route. Exact distance is calculated live when you run the comparison." },
                { q: "How much is a taxi from Reading to Heathrow?", a: "We show the two-way taxi cost (outbound and return) using live pricing ranges and benchmarks where available." },
                { q: "Is Long Stay parking worth it for Reading travellers?", a: "Often for 7+ day trips. Check your exact numbers above to see if parking beats two taxis." },
                { q: "What happens if my return flight is delayed?", a: "Parking time may extend; taxis avoid extra parking time but can be pricier overall. Re-run the calculator if timings change." },
                { q: "Is a taxi better for families travelling from Reading?", a: "Taxis remove post-flight driving fatigue. The calculator shows the price trade-off versus parking for your Reading trip." }
              ].map(({ q, a }) => (
                <details key={q} className="train-collapsible" open={false}>
                  <summary className="train-summary-bar" style={{ fontWeight: 700, fontSize: 15 }}>{q}</summary>
                  <div className="train-collapsible-body" style={{ fontSize: 14, color: "#1f2937" }}>{a}</div>
                </details>
              ))}
            </div>
          </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
        Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
      </div>
    </div>
  )}

      <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
        <strong>Plan your Heathrow journey:</strong>
        <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
          <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
          <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
          <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
          <li>✈️ <a href="/flight-checker">Check your flight</a></li>
          <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
          <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
          <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
        </ul>
      </div>

      <div style={{ marginTop: 30 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
